import { getMediaSinglePixelWidth } from '@atlaskit/editor-common/media-single';
import { MEDIA_SINGLE_GUTTER_SIZE } from '@atlaskit/editor-common/media-single/constants';
import { calcMediaSinglePixelWidth } from '@atlaskit/editor-common/media-single/utils';
import type { Fragment, Node } from '@atlaskit/editor-prosemirror/model';
import { akEditorDefaultLayoutWidth } from '@atlaskit/editor-shared-styles/constants';
import { defaultImageCardDimensions } from '@atlaskit/media-card/cardDimensions';

import { expVal } from '@confluence/feature-experiments';

const NON_FLOATING_MEDIA_LAYOUTS = ['align-start', 'center', 'align-end', 'wide', 'full-width'];
const RENDERED_MEDIA_TYPES = ['file', 'external'];

const HEADING_HEIGHTS: Record<number, number> = {
	1: 32,
	2: 28,
	3: 28,
	4: 18,
	5: 18,
	6: 18,
};

const NODES_WITH_NO_CHILDREN: Record<string, { height: number }> = {
	blockCard: { height: 114 },
	caption: { height: 32 },
	date: { height: 0 },
	emoji: { height: 0 },
	inlineCard: { height: 0 },
	mediaInline: { height: 0 },
	mention: { height: 0 },
	placeholder: { height: 0 },
	rule: { height: 2 },
	status: { height: 0 },
	// expand + nestedExpand have children, but they are collapsed by default. So, we treat them as having no children for height estimation.
	expand: { height: 40 },
	nestedExpand: { height: 40 },
};

const NODES_WITH_TEXT_CHILDREN: Record<string, { contentHeight: number }> = {
	decisionItem: { contentHeight: 36 },
	paragraph: { contentHeight: 28 },
	taskItem: { contentHeight: 28 },
};

const NODES_WITH_INLINE_CHILDREN: Record<
	string,
	{ additionalHeight: number; minimumHeight: number }
> = {
	layoutSection: { additionalHeight: 44, minimumHeight: 72 },
	tableRow: { additionalHeight: 0, minimumHeight: 48 },
};

const NODES_WITH_BLOCK_CHILDREN: Record<
	string,
	{ additionalHeight: number; minimumHeight: number }
> = {
	blockquote: { additionalHeight: 20, minimumHeight: 48 },
	blockTaskItem: { additionalHeight: 0, minimumHeight: 28 },
	bulletList: { additionalHeight: 0, minimumHeight: 0 },
	decisionList: { additionalHeight: 0, minimumHeight: 0 },
	layoutColumn: { additionalHeight: 0, minimumHeight: 28 },
	listItem: { additionalHeight: 0, minimumHeight: 28 },
	orderedList: { additionalHeight: 0, minimumHeight: 0 },
	panel: { additionalHeight: 20, minimumHeight: 48 },
	panel_c1: { additionalHeight: 20, minimumHeight: 48 },
	table: { additionalHeight: 20, minimumHeight: 68 },
	tableCell: { additionalHeight: 20, minimumHeight: 48 },
	tableHeader: { additionalHeight: 20, minimumHeight: 48 },
	taskList: { additionalHeight: 0, minimumHeight: 0 },
};

const TABLE_OF_CONTENTS_ENTRY_HEIGHT = 28;
const TABLE_OF_CONTENTS_SPACING = 10;

const isPositiveDimension = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value) && value > 0;

type HeightEstimationOptions = {
	/** Count floating media's own height for light-mode batching, even when text shares its space. */
	estimateFloatingMedia?: boolean;
	/** The document content whose headings a table of contents lists. */
	documentContent?: Fragment;
};

const headingCountsByContent = new WeakMap<Fragment, number[]>();

// Like the table of contents, count nested headings and skip empty ones.
const getHeadingCounts = (content: Fragment): number[] => {
	const cached = headingCountsByContent.get(content);
	if (cached) {
		return cached;
	}
	const counts = [0, 0, 0, 0, 0, 0, 0];
	content.descendants((node) => {
		if (node.type.name === 'heading' && node.childCount > 0) {
			counts[node.attrs.level]++;
		}
		// Headings never sit inside text blocks, so inline content is skipped.
		return !node.isTextblock;
	});
	headingCountsByContent.set(content, counts);
	return counts;
};

const isTableOfContents = (node: Node): boolean =>
	node.type.name === 'extension' &&
	node.attrs.extensionType === 'com.atlassian.confluence.macro.core' &&
	node.attrs.extensionKey === 'toc';

// Headings that only appear once included content loads are not counted.
const getTableOfContentsHeight = (
	node: Node,
	documentContent: Fragment | undefined,
): number | undefined => {
	const macroParams = node.attrs.parameters?.macroParams;
	if (
		!documentContent ||
		// Entries filtered by the macro's regular expressions stay unknown.
		macroParams?.include?.value ||
		macroParams?.exclude?.value ||
		!expVal('confluence_toc_height_estimate', 'isEnabled', false)
	) {
		return undefined;
	}
	const minLevel = Math.max(1, parseInt(macroParams?.minLevel?.value, 10) || 1);
	const maxLevel = Math.min(6, parseInt(macroParams?.maxLevel?.value, 10) || 6);
	const counts = getHeadingCounts(documentContent);
	let entries = 0;
	for (let level = minLevel; level <= maxLevel; level++) {
		entries += counts[level];
	}
	if (entries === 0) {
		return 0;
	}
	// A flat table of contents puts its entries on one line.
	const lines = macroParams?.type?.value === 'flat' ? 1 : entries;
	return lines * TABLE_OF_CONTENTS_ENTRY_HEIGHT + TABLE_OF_CONTENTS_SPACING;
};

const getMediaSingleEstimatedHeight = (
	node: Node,
	options: HeightEstimationOptions,
): number | undefined => {
	const media = node.firstChild;
	if (media?.type.name !== 'media') {
		return undefined;
	}
	const { width, height } = media.attrs;
	if (!isPositiveDimension(width) || !isPositiveDimension(height)) {
		return undefined;
	}
	const { layout, width: authoredWidth, widthType } = node.attrs;
	const isFloating = layout === 'wrap-left' || layout === 'wrap-right';
	// Default estimates omit floats because following content can share their vertical space.
	if (
		!NON_FLOATING_MEDIA_LAYOUTS.includes(layout) &&
		!(options.estimateFloatingMedia && isFloating)
	) {
		return undefined;
	}
	// Match the media renderer's default SSR width; the actual container width is unavailable here.
	let renderedWidth = Math.min(width, akEditorDefaultLayoutWidth);
	if (authoredWidth !== undefined && authoredWidth !== null) {
		if (!isPositiveDimension(authoredWidth) || (widthType !== 'pixel' && authoredWidth > 100)) {
			return undefined;
		}
		renderedWidth = getMediaSinglePixelWidth(
			authoredWidth,
			akEditorDefaultLayoutWidth,
			widthType,
			MEDIA_SINGLE_GUTTER_SIZE,
		);
	} else if (layout === 'wide' || layout === 'full-width') {
		renderedWidth = akEditorDefaultLayoutWidth;
	} else if (layout === 'align-start' || layout === 'align-end') {
		renderedWidth = Math.min(
			renderedWidth,
			(akEditorDefaultLayoutWidth - MEDIA_SINGLE_GUTTER_SIZE) / 2,
		);
	}
	if (isFloating) {
		// Match the light figure's sizing rules using the estimator's assumed content width.
		renderedWidth = Math.min(
			calcMediaSinglePixelWidth({
				containerWidth: akEditorDefaultLayoutWidth,
				contentWidth: akEditorDefaultLayoutWidth,
				gutterOffset: MEDIA_SINGLE_GUTTER_SIZE,
				layout,
				origWidth: width,
				width: authoredWidth,
				widthType,
			}),
			akEditorDefaultLayoutWidth,
		);
	}
	const estimatedHeight = (Math.max(0, renderedWidth) / width) * height;
	const captionHeight =
		node.maybeChild(1)?.type.name === 'caption' ? NODES_WITH_NO_CHILDREN.caption.height : 0;
	return Number.isFinite(estimatedHeight) ? estimatedHeight + captionHeight : undefined;
};

const getMediaGroupEstimatedHeight = (node: Node): number => {
	if (node.childCount === 0) {
		// The empty filmstrip still renders its wrapper spacing.
		return 32;
	}
	// Filmstrip cards share a horizontal row; link nodes are not rendered by MediaGroup.
	for (let index = 0; index < node.childCount; index++) {
		const child = node.child(index);
		if (child.type.name === 'media' && RENDERED_MEDIA_TYPES.includes(child.attrs.type)) {
			return defaultImageCardDimensions.height;
		}
	}
	return 0;
};

export interface ChildInfoOutput {
	estimatedChildHeights: number[];
}

const getLineCount = (node: Node): number => {
	let lineCount = 1;
	node.content.forEach((child) => {
		if (child.type.name === 'hardBreak') {
			lineCount++;
		}
	});
	return lineCount;
};

// Typography, spacing, and container width can make these SSR estimates drift.
// Prefer underestimates so progressive rendering keeps more content mounted.
export const getEstimatedHeight = (
	node: Node,
	nodeChildInfoOutput: ChildInfoOutput,
	options: HeightEstimationOptions = {},
): number | undefined => {
	const nodeTypeName = node?.type?.name || '';
	if (nodeTypeName === 'text' || nodeTypeName === 'hardBreak') {
		// Their containing paragraph or heading accounts for inline text and line breaks.
		return 0;
	} else if (NODES_WITH_NO_CHILDREN[nodeTypeName]) {
		return NODES_WITH_NO_CHILDREN[nodeTypeName].height || 0;
	} else if (NODES_WITH_INLINE_CHILDREN[nodeTypeName]) {
		const additionalHeight = NODES_WITH_INLINE_CHILDREN[nodeTypeName].additionalHeight || 0;
		const minimumHeight = NODES_WITH_INLINE_CHILDREN[nodeTypeName].minimumHeight || 0;
		const childrenHeight = Math.max(
			...nodeChildInfoOutput.estimatedChildHeights.map((height) => height || 0),
			// 0 is added in case there are no entries in estimatedChildHeights.
			0,
		);
		return Math.max(childrenHeight + additionalHeight, minimumHeight);
	} else if (NODES_WITH_BLOCK_CHILDREN[nodeTypeName]) {
		const additionalHeight = NODES_WITH_BLOCK_CHILDREN[nodeTypeName].additionalHeight || 0;
		const minimumHeight = NODES_WITH_BLOCK_CHILDREN[nodeTypeName].minimumHeight || 0;
		const childrenHeight = nodeChildInfoOutput.estimatedChildHeights.reduce(
			(acc, height) => acc + (height || 0),
			0,
		);
		return Math.max(childrenHeight + additionalHeight, minimumHeight);
	} else if (NODES_WITH_TEXT_CHILDREN[nodeTypeName]) {
		const contentHeight = NODES_WITH_TEXT_CHILDREN[nodeTypeName].contentHeight || 0;
		return getLineCount(node) * contentHeight;
	} else if (nodeTypeName === 'heading') {
		const contentHeight = HEADING_HEIGHTS[node?.attrs?.level] || 0;
		return getLineCount(node) * contentHeight;
	} else if (nodeTypeName === 'mediaSingle') {
		return getMediaSingleEstimatedHeight(node, options);
	} else if (nodeTypeName === 'mediaGroup') {
		return getMediaGroupEstimatedHeight(node);
	} else if (
		nodeTypeName === 'embedCard' &&
		NON_FLOATING_MEDIA_LAYOUTS.includes(node?.attrs?.layout)
	) {
		// The height of embedCards is likely much larger than 58, but it depends on a number of factors.
		// This is used as a safe simple value which may be improved upon in the future.
		return 58;
	} else if (nodeTypeName === 'codeBlock') {
		const additionalHeight = 16;
		const minimumHeight = 40;

		// Text takes up at least 24px per line, so we multiply the number of lines by 24.
		const contentHeight = node.textContent.split('\n').length * 24;
		return Math.max(contentHeight + additionalHeight, minimumHeight);
	} else if (isTableOfContents(node)) {
		return getTableOfContentsHeight(node, options.documentContent);
	}
	return undefined;
};

export const getRowEstimatedHeight = (node: Node): number | undefined => {
	if (node.type.name === 'mediaSingle' || node.type.name === 'mediaGroup') {
		return getEstimatedHeight(node, { estimatedChildHeights: [] });
	}
	const estimatedChildHeights: number[] = [];
	for (let index = 0; index < node.childCount; index++) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- Visits each child subtree once; work is linear in the number of nodes.
		const height = getRowEstimatedHeight(node.child(index));
		if (height === undefined) {
			return undefined;
		}
		estimatedChildHeights.push(height);
	}
	return getEstimatedHeight(node, { estimatedChildHeights });
};

const heights = new WeakMap<Node, number>();

export const getTableEstimatedHeight = (node: Node): number => {
	const cached = heights.get(node);
	if (cached !== undefined) {
		return cached;
	}
	const estimatedChildHeights: number[] = [];
	if (NODES_WITH_INLINE_CHILDREN[node.type.name] || NODES_WITH_BLOCK_CHILDREN[node.type.name]) {
		for (let index = 0; index < node.childCount; index++) {
			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- Each immutable node is visited once and its height is cached.
			estimatedChildHeights.push(getTableEstimatedHeight(node.child(index)));
		}
	}
	// Unknown preserved content contributes no height to this lower-bound placeholder estimate.
	const height = getEstimatedHeight(node, { estimatedChildHeights }) ?? 0;
	heights.set(node, height);
	return height;
};

export class IncrementalHeightEstimator {
	private estimatedHeight = 0;
	private childInfoStack: ChildInfoOutput[] = [];

	constructor(private options: HeightEstimationOptions = {}) {}

	get height(): number {
		return this.estimatedHeight;
	}

	enterNode(): void {
		this.childInfoStack.push({ estimatedChildHeights: [] });
	}

	leaveNode(path?: readonly Node[]): number {
		const childInfo = this.childInfoStack.pop() ?? { estimatedChildHeights: [] };
		const node = path?.[path.length - 1];
		const height = node ? (getEstimatedHeight(node, childInfo, this.options) ?? 0) : 0;
		this.childInfoStack[this.childInfoStack.length - 1]?.estimatedChildHeights.push(height);

		if (path?.length === 1) {
			this.estimatedHeight += height;
		}
		return this.estimatedHeight;
	}
}
